import { html, nothing, type TemplateResult } from "lit";
import type { MapKeyOption } from "../types.ts";
import { t } from "../i18n.ts";

export type ChannelPickerProps = {
  options: MapKeyOption[];
  existingKeys: Set<string>;
  onSelect: (key: string) => void;
  onClose: () => void;
};

function difficultyBadge(difficulty?: string): TemplateResult | typeof nothing {
  if (!difficulty) {
    return nothing;
  }
  const cls =
    difficulty === "easy"
      ? "channel-picker__badge--easy"
      : difficulty === "hard"
        ? "channel-picker__badge--hard"
        : "channel-picker__badge--medium";
  const label =
    difficulty === "easy"
      ? t("picker.easy")
      : difficulty === "hard"
        ? t("picker.hard")
        : t("picker.medium");
  return html`<span class="channel-picker__badge ${cls}">${label}</span>`;
}

export function renderChannelPicker(props: ChannelPickerProps): TemplateResult {
  const available = props.options.filter((opt) => !props.existingKeys.has(opt.key));
  const alreadyAdded = props.options.filter((opt) => props.existingKeys.has(opt.key));

  return html`
    <div
      class="channel-picker__overlay"
      @click=${(e: Event) => {
        if ((e.target as HTMLElement).classList.contains("channel-picker__overlay")) {
          props.onClose();
        }
      }}
    >
      <div class="channel-picker__dialog">
        <div class="channel-picker__header">
          <div>
            <div class="channel-picker__title">${t("picker.title")}</div>
            <div class="channel-picker__sub">${t("picker.subtitle")}</div>
          </div>
          <button
            class="channel-picker__close"
            @click=${() => props.onClose()}
            title="${t("common.cancel")}"
          >
            &times;
          </button>
        </div>

        ${
          available.length > 0
            ? html`
                <div class="channel-picker__grid">
                  ${available.map(
                    (opt) => html`
                    <div class="channel-picker__card">
                      <div class="channel-picker__card-body">
                        <div class="channel-picker__card-name">${opt.label}</div>
                        ${opt.description ? html`<div class="channel-picker__card-desc">${opt.description}</div>` : nothing}
                        ${difficultyBadge(opt.difficulty)}
                      </div>
                      <button
                        class="btn btn--sm primary channel-picker__card-btn"
                        @click=${() => props.onSelect(opt.key)}
                      >
                        ${t("picker.add")}
                      </button>
                    </div>
                  `,
                  )}
                </div>
              `
            : html`
                <div class="channel-picker__empty">
                  ${t("picker.all_added")}
                </div>
              `
        }

        ${
          alreadyAdded.length > 0
            ? html`
                <div class="channel-picker__added-section">
                  <div class="channel-picker__added-label">${t("picker.already_added")}</div>
                  <div class="channel-picker__added-list">
                    ${alreadyAdded.map(
                      (opt) => html`
                      <span class="channel-picker__added-tag">${opt.label}</span>
                    `,
                    )}
                  </div>
                </div>
              `
            : nothing
        }

        <div class="channel-picker__footer">
          <button class="btn" @click=${() => props.onClose()}>
            ${t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  `;
}
